import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Tabs, TabsItemOnOrderChange } from "../../components/tabs";
import { Flex } from "@common/components/Flex";
import client, { clientTransformed } from "@common/utils/rest";
import { waitMs } from "@common/utils/mock";
import { FolderContext } from "@common/dto/folder/context";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { getTestAttributes } from "@common/utils/tests";

import { Header } from "./components/header";
import { FieldPicker } from "./components/field-picker";
import { DatasheetElementSettings } from "./components/datasheet-element-settings";
import { DatasheetSaveAs } from "./components/datasheet-save-as";
import { Datasheet } from "./components/datasheet";
import { DatasheetFormatManagement } from "../datasheet-format-management";
import { DatasheetFormatManagementMode } from "../datasheet-format-management/datasheet-format-management.types";
import { EditorProps, useEditorController } from "./controllers/main";
import { DatasheetElement } from "@modules/datasheets/types/datasheet-element";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetPrintingSettings } from "../datasheet-printing-settings";

import "./editor.styles.scss";

export const Editor: React.FC<EditorProps> = (props) => {
  const {
    copiedDatasheetElementsCount,
    handleDatasheetElementSelect,
    handleSelectionReset,
    isDatasheetElementSelected,
    selectedFieldsInFieldPicker,
    handleSelectedFieldsInFieldPickerChange,
    handleDatasheetElementsDrop,
    isFieldAddedToDatasheet,
    datasheetElementsProcessed,
    handleDatasheetElementChange,
    handleSelectionEnd,
    selectedDatasheetElementsCount,
    datasheet,
    currentTabId,
    handleSelectedTabChange,
    handleTabOrderChange,
    folderPickerOpened,
    openFolderPicker,
    closeFolderPicker,
    handleSaveAsSubmit,
    handleGridSettingsChange,
    datasheetFormatManagementMode,
    closeDatasheetFormatManagement,
    handleDatasheetFormatManagementSubmit,
    setDatasheetFormatManagementMode,
    defaultFieldComponentSettings,
    setDefaultFieldComponentSettings,
    handleFieldComponentSettingsChange,
    currentFieldComponentMetadata,
    handleDatasheetElementsAlignmentChange,
    handleDatasheetElementsSizeChange,
    handleSelectAllDatasheetElements,
    handleDatasheetTextElementsDrop,
    handleDatasheetElementsCopy,
    handleDatasheetElementsPaste,
    gridSettingsMargin,
    pageBreaks,
    handleDatasheetElementsTabOrderChange,
    handleResetTabOrder,
    handleDatasheetElementResize,
    handleDatasheetElementMove,
    canUndo,
    handleDatasheetElementsUndo,
    canRedo,
    handleDatasheetElementsRedo,
    handlePrintDatasheetElements,
    handlePrintingSettingsClose,
    printingSettingsOpened,
    calculatePaperCount,
    handlePrintWithPageSkip,
    handleTabRename,
    pastingOfTabElementsAllowed,
    handleTabAdd,
    handleTabCopy,
    handleTabPaste,
    handleTabDelete,
    printableDatasheetHolderId,
  } = useEditorController(props);

  const datasheetElementDndType = "datasheet_editor_element";

  return (
    <div className="DatasheetsEditor">
      <Header
        className="DatasheetsEditor__header"
        disabledState={{
          addText: datasheet.gridSettings.showEditTabOrder,
          tabs: datasheet.gridSettings.showEditTabOrder,
          print: datasheet.gridSettings.showEditTabOrder,
          undo: datasheet.gridSettings.showEditTabOrder || !canUndo,
          redo: datasheet.gridSettings.showEditTabOrder || !canRedo,
          datasheetElementCopy: copiedDatasheetElementsCount > 0 ? true : selectedDatasheetElementsCount === 0,
          datasheetElementPaste: copiedDatasheetElementsCount === 0,
          selectAll: datasheet.gridSettings.showEditTabOrder,
          alignment: datasheet.gridSettings.showEditTabOrder,
          tabPaste: !pastingOfTabElementsAllowed,
        }}
        onAddText={handleDatasheetTextElementsDrop}
        onSave={() => {}}
        onSaveAs={openFolderPicker}
        onPrint={handlePrintDatasheetElements}
        onUndo={handleDatasheetElementsUndo}
        onRedo={handleDatasheetElementsRedo}
        onDatasheetElementCopy={handleDatasheetElementsCopy}
        onDatasheetElementPaste={handleDatasheetElementsPaste}
        onSelectAll={handleSelectAllDatasheetElements}
        onLoadDatasheet={() => setDatasheetFormatManagementMode(DatasheetFormatManagementMode.load)}
        onNewDatasheet={() => setDatasheetFormatManagementMode(DatasheetFormatManagementMode.new)}
        datasheet={datasheet}
        onDatasheetElementAlignmentChange={handleDatasheetElementsAlignmentChange}
        onDatasheetElementSizeAlignmentChange={handleDatasheetElementsSizeChange}
        onGridSettingsChange={handleGridSettingsChange}
        onResetTabOrder={handleResetTabOrder}
        onTabAdd={handleTabAdd}
        onTabCopy={handleTabCopy}
        onTabPaste={handleTabPaste}
        onTabDelete={handleTabDelete}
        onTabRename={handleTabRename}
      />
      {
        printingSettingsOpened && (
          <DatasheetPrintingSettings onClose={handlePrintingSettingsClose} calculatePaperCount={calculatePaperCount} onSubmit={handlePrintWithPageSkip} />
        )
      }
      {datasheetFormatManagementMode && (
        <DatasheetFormatManagement
          onClose={closeDatasheetFormatManagement}
          getId={{
            title: getTestAttributes({ id: "lbl-new-data-sheet-wnd-header" }).id,
            folderChooserBtn: getTestAttributes({ id: "fp-new-data-sheet-wnd-folder-picker" }).id,
            formatTypes: getTestAttributes({ id: "new-data-sheet-wnd-format-type-drop-btn" }).id,
            selectedFolderNameHolder: getTestAttributes({ id: "tx-new-data-sheet-wnd-folder" }).id,
            formatName: getTestAttributes({ id: "tx-new-data-sheet-wnd-ds-format-name" }).id,
          }}
          onSubmit={handleDatasheetFormatManagementSubmit}
          getFormats={() =>
            Promise.resolve([
              {
                id: 1,
                name: "test format",
              },
            ])
          }
          getFolders={() =>
            Promise.resolve([
              {
                id: 1,
                name: "test folder",
                parentid: 0,
              },
            ] as Array<FolderPrimaryListDto>)
          }
          mode={datasheetFormatManagementMode}
          checkFormatNameExists={() => Promise.resolve(false)}
        />
      )}
      {folderPickerOpened && (
        <DatasheetSaveAs
          deleteFormat={async (folderId: number, formatId: number) => {
            await waitMs(1000);
          }}
          deleteFolder={async (folderId: number) => {
            await waitMs(1000);
          }}
          createFolder={async (data: { parentid?: number; foldername: string }) => {
            await waitMs(1000);
          }}
          getFolders={async () =>
            Promise.resolve([
              {
                id: 1,
                name: "test folder",
                parentid: 0,
                secToken: 0,
                system: false,
                context: FolderContext.individual,
                imageURL: '',
                imageURLSelected: ''
              },
            ])
          }
          getFormats={async (folderId: number) =>
            Promise.resolve([
              {
                id: 1,
                name: "test format",
              },
            ])
          }
          onSubmit={handleSaveAsSubmit}
          onClose={closeFolderPicker}
        />
      )}
      <Flex className="DatasheetsEditor__content">
        <DndProvider backend={HTML5Backend} options={{ enableTouchEvents: false, enableMouseEvents: true }}>
          <FieldPicker 
            disabled={datasheet.gridSettings.showEditTabOrder}
            isFieldAdded={isFieldAddedToDatasheet}
            dndType={datasheetElementDndType}
            selection={{
              items: selectedFieldsInFieldPicker,
              onChange: handleSelectedFieldsInFieldPickerChange,
            }}
            className="DatasheetsEditor__section"
            getFolders={() => client({
              path: 'api/folders/module/loadFolders',
              params: {
                folderTypeId: 1,
              },
              method: 'GET',
            }).then((x: any) => x.entity)}
            getFields={({ searchQuery, folderId, context }) => {
              return clientTransformed({
                path: "api/fields",
                params: {
                  query: searchQuery,
                  folderId,
                  conext: context,
                },
                method: "GET",
            }).then((x: any) => x.entity)}}
          />
          <div className="DatasheetsEditor__section DatasheetsEditor__datasheet">
            {currentTabId && (
              <Tabs 
                items={datasheet.tabs}
                value={currentTabId}
                onChange={handleSelectedTabChange}
                classes={{
                  tabs: "DatasheetsEditor__datasheet__tabs"
                }}
                onTabOrderChange={handleTabOrderChange as TabsItemOnOrderChange}
              />
            )}
            <Datasheet 
              printableDatasheetHolderId={printableDatasheetHolderId}
              onTabOrderChange={handleDatasheetElementsTabOrderChange}
              pageBreaks={pageBreaks}
              gridSettingsMargin={gridSettingsMargin}
              onDatasheetElementUpdate={handleDatasheetElementChange}
              onDatasheetElementMove={handleDatasheetElementMove}
              onDatasheetElementResize={handleDatasheetElementResize}
              datasheet={datasheet}
              selectedDatasheetElementsCount={selectedDatasheetElementsCount}
              onDatasheetElementSelect={handleDatasheetElementSelect}
              isDatasheetElementSelected={isDatasheetElementSelected}
              onSelectionEnd={handleSelectionEnd}
              onSelectionReset={handleSelectionReset}
              datasheetElements={datasheetElementsProcessed}
              onDatasheetElementsDrop={handleDatasheetElementsDrop}
              datasheetElementDragType={datasheetElementDndType} 
              className="DatasheetsEditor__datasheet__component"
            />
          </div>
        </DndProvider>
        <DatasheetElementSettings 
          className="DatasheetsEditor__section" 
          // TODO should be full path to field
          title={(currentFieldComponentMetadata?.datasheetElement as DatasheetElement<DatasheetFieldComponentGetDto> | undefined)?.original.headtext}
          textComponentValue={undefined}
          fieldComponentValue={currentFieldComponentMetadata?.fieldComponentValue}
          defaultFieldComponentValue={defaultFieldComponentSettings}
          onDefaultFieldComponentChange={setDefaultFieldComponentSettings}
          onFieldComponentChange={handleFieldComponentSettingsChange} 
          onTextComponentChange={console.log}
        />
      </Flex>
    </div>
  );
};